import { useCallback, useState } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { useShallow } from "zustand/react/shallow";

import { TGTGBagAdditionMethod } from "analytics/events";
import { useAppLanguageStore } from "app/stores/useAppLanguageStore";
import { useBarcodeScanner } from "shared/hooks/useBarcodeScanner";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { serializeSearchUnitWithPriceResult } from "shared/models/products/serializer";
import { useSearchUnitsByEanWithPriceLazyQuery } from "shared/queries/products/products.generated";
import { countryNameFromCode } from "utils/countryNames";

import { useAddProductToBag } from "./useBagProductActions";
import { useTGTGBagStore } from "../stores/useTGTGBagStore";
import { isProductEligibleForBag } from "../utils/helpers";

const GET_UNITS_BY_EAN_ERROR_TOAST_ID = "get_units_by_ean_error_toast_id";

export function useFetchProductsByEanWithPrice() {
  const appLanguage = useAppLanguageStore((state) => state.appLanguage);
  const {
    setProductToAdd,
    setShowProductNotListedModal,
    setShowProductNotEligibleModal,
    allowedCategories,
    excludedCategories,
  } = useTGTGBagStore(
    useShallow((state) => ({
      setProductToAdd: state.setProductToAdd,
      setShowProductNotListedModal: state.setShowProductNotListedModal,
      setShowProductNotEligibleModal: state.setShowProductNotEligibleModal,
      allowedCategories: state.allowedCategories,
      excludedCategories: state.excludedCategories,
    })),
  );
  const addProductToBag = useAddProductToBag();
  const [isScannedProductLoading, setIsScannedProductLoading] = useState(false);
  const { showToastUI } = useCustomToast();
  const { processBarcode } = useBarcodeScanner();

  const handleError = useCallback(
    (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Error while fetching units by ean", { errorMessage });
      showToastUI({
        id: GET_UNITS_BY_EAN_ERROR_TOAST_ID,
        title: "get_units_by_ean_error_toast_id",
        description: "get_units_by_ean_error_toast_description",
      });
    },
    [showToastUI],
  );

  const [searchUnitsByEan] = useSearchUnitsByEanWithPriceLazyQuery({
    fetchPolicy: "no-cache",
    onError: handleError,
  });

  const onScan = useCallback(
    async (scannedValue: string) => {
      const ean = processBarcode(scannedValue);
      if (!ean) return;

      setIsScannedProductLoading(true);
      try {
        const { data } = await searchUnitsByEan({
          variables: {
            searchUnitsByEanInput: { ean },
            isAlwaysInbound: true,
          },
        });
        const units =
          data?.searchUnitsByEan?.units.filter(
            (unit) => unit.type === "single" || unit.type === "variable-price",
          ) ?? [];
        if (!units.length) {
          setProductToAdd({
            id: "",
            ean,
            productSku: "",
            productName: null,
            productImageUrl: null,
            productPrice: null,
            countryOfOriginCode: null,
            countryOfOriginName: null,
            productClassification: null,
          });
          setShowProductNotListedModal(true);
          return;
        }

        const uniqueProducts = Array.from(
          new Map(
            units.map((unit) => {
              const serializedProduct = serializeSearchUnitWithPriceResult(unit);
              return [
                serializedProduct.productSku,
                {
                  ...serializedProduct,
                  countryOfOriginName:
                    countryNameFromCode(appLanguage, serializedProduct.countryOfOriginCode) ?? "",
                },
              ];
            }),
          ).values(),
        );
        const notEligibleProduct = uniqueProducts.find(
          (product) =>
            !isProductEligibleForBag(
              product.productClassification,
              allowedCategories,
              excludedCategories,
            ),
        );
        if (notEligibleProduct) {
          setShowProductNotEligibleModal(true, notEligibleProduct.productName);
          return;
        }

        const productWithoutPrice = uniqueProducts.find((product) => product.productPrice === null);
        if (productWithoutPrice) {
          setProductToAdd(productWithoutPrice);
          setShowProductNotListedModal(true);
          return;
        }
        uniqueProducts.forEach((product) =>
          addProductToBag(product, TGTGBagAdditionMethod.Scanned),
        );
      } catch (error) {
        handleError(error);
      } finally {
        setIsScannedProductLoading(false);
      }
    },
    [
      addProductToBag,
      appLanguage,
      handleError,
      processBarcode,
      searchUnitsByEan,
      allowedCategories,
      excludedCategories,
      setProductToAdd,
      setShowProductNotEligibleModal,
      setShowProductNotListedModal,
    ],
  );

  return { onScan, isScannedProductLoading };
}
